import { useMetaQuery } from '../../api/queries';
import { formatNumber } from '../../lib/format';
import MetricCard from '../../components/MetricCard';
import SubTabLoading from './SubTabLoading';
import type { MetaCreative } from '../../types';

type Props = { af: string };

export default function MetaSubTab({ af }: Props) {
  const { data, isLoading, error } = useMetaQuery(af);

  if (isLoading) {
    return (
      <SubTabLoading
        title="Loading Meta Ads Data..."
        sub="Fetching metrics and creatives from the Meta Marketing API"
      />
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>Meta Ads Data Unavailable</h3>
        <p>{(error as Error).message}</p>
      </div>
    );
  }
  if (!data || !data.found) {
    return (
      <div className="tab-placeholder">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{ opacity: 0.3, marginBottom: 12 }}
        >
          <path d="M22 12a10 10 0 1 0-11.5 9.87v-6.99H8v-2.88h2.5V9.83c0-2.47 1.46-3.84 3.7-3.84 1.07 0 2.2.2 2.2.2v2.42h-1.24c-1.22 0-1.6.76-1.6 1.54v1.85h2.72l-.43 2.88h-2.29v6.99A10 10 0 0 0 22 12z" />
        </svg>
        <h3>No Active Meta Campaign</h3>
        <p>This franchisee has no active campaigns matching {af} on Meta.</p>
      </div>
    );
  }

  const { current, changes, dateRange, creatives, campaigns } = data;
  const period = (
    <span style={{ fontSize: 9, fontWeight: 400, letterSpacing: '0.3px' }}> · Last 90 Days</span>
  );

  return (
    <>
      <div className="metrics-row-4">
        <MetricCard
          label={<>Clicks{period}</>}
          value={formatNumber(current.clicks)}
          change={changes.clicks}
        />
        <MetricCard
          label={<>CTR{period}</>}
          value={`${current.ctr.toFixed(2)}%`}
          change={changes.ctr}
        />
        <MetricCard
          label={<>Impressions{period}</>}
          value={formatNumber(current.impressions)}
          change={changes.impressions}
        />
        <MetricCard
          label={<>Conversions{period}</>}
          value={formatNumber(Math.round(current.conversions))}
          change={changes.conversions}
        />
      </div>

      <div className="metrics-row-4">
        <MetricCard
          label={<>Conv. Rate{period}</>}
          value={`${current.convRate.toFixed(2)}%`}
          change={changes.convRate}
        />
        <MetricCard
          label={<>Cost / Conv{period}</>}
          value={current.conversions > 0 ? `$${current.costPerConv.toFixed(2)}` : '—'}
          change={changes.costPerConv}
          positiveIsGood={false}
        />
        <MetricCard
          label={<>Cost{period}</>}
          value={`$${formatNumber(Math.round(current.spend))}`}
          change={changes.spend}
          positiveIsGood={false}
        />
        <MetricCard
          label={<>Avg. CPC{period}</>}
          value={`$${current.cpc.toFixed(2)}`}
          change={changes.cpc}
          positiveIsGood={false}
        />
      </div>

      {creatives.length > 0 && (
        <div className="meta-creatives">
          <h3 className="meta-creatives-title">Active Ad Creatives</h3>
          <div className="meta-creatives-grid">
            {creatives.map((c) => (
              <CreativeCard key={c.adId} creative={c} />
            ))}
          </div>
        </div>
      )}

      <div className="metrics-footer">
        Meta Ads — Last 90 Days • {dateRange} •{' '}
        {campaigns.length} campaign{campaigns.length === 1 ? '' : 's'} matched
      </div>
    </>
  );
}

function CreativeCard({ creative }: { creative: MetaCreative }) {
  const img = creative.imageUrl ?? creative.thumbnailUrl;
  const externalLink =
    creative.facebookPermalinkUrl ?? creative.instagramPermalinkUrl ?? null;
  return (
    <div className="meta-creative-card">
      {img ? (
        <div className="meta-creative-image">
          <img src={img} alt={creative.title ?? creative.adName} loading="lazy" />
        </div>
      ) : (
        <div className="meta-creative-image meta-creative-image-empty">No preview</div>
      )}
      <div className="meta-creative-body">
        {creative.title && <div className="meta-creative-title">{creative.title}</div>}
        {creative.body && <div className="meta-creative-text">{creative.body}</div>}
        <div className="meta-creative-meta">
          {creative.callToAction && (
            <span className="meta-creative-cta">{creative.callToAction.replace(/_/g, ' ')}</span>
          )}
          {externalLink && (
            <a
              href={externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="meta-creative-link"
            >
              View on Facebook ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
